import { NON_PLAYER_ROLE_OPTIONS } from '../../../../utils/constants';
import SwitchButton from '../../Custom/Switch';
import FormField from './FormField';

/**
 * AvailabilitySection Component
 *
 * @param {Object} param0 - Component props
 * @param {Object} param0.values - Form values
 * @param {Object} param0.errors - Form errors
 * @param {Object} param0.touched - Touched fields
 * @param {Function} param0.handleChange - Change handler
 * @param {Function} param0.handleBlur - Blur handler
 * @param {Object} param0.userInfo - User information
 * @param {Function} param0.handleRoleChange - Role change handler
 * @param {import("../../Custom/DropdownWithCheckBox").default} param0.DropdownWithCheckBox - Dropdown component with checkbox
 * @param {Array} param0.ListOfCountries - List of countries for dropdown options
 * @param {Array} param0.ListOfNationalities - List of nationalities for dropdown options
 * @returns {JSX.Element}
 */
const AvailabilitySection = ({
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    userInfo,
    handleRoleChange,
    DropdownWithCheckBox,
    ListOfCountries,
    ListOfNationalities
}) => {
    return (
        <section className='availability-section'>
            {userInfo.userType !== 'NON_PLAYER' && (
                <>
                    <h3 className='text-[14px] font-semibold my-5'>
                        Availability
                    </h3>

                    <FormField
                        name='eligibleNations'
                        label='National Eligibility'
                        error={errors.eligibleNations}
                        touched={touched.eligibleNations}
                    >
                        <DropdownWithCheckBox
                            maxSelection={3}
                            label='Select Nation(s)'
                            defaultOptions={
                                userInfo.eligibleNations?.map(
                                    item => item.id
                                ) || []
                            }
                            valueKey='id'
                            labelKey='name'
                            extendedView
                            listOfOptions={ListOfNationalities || []}
                            onChange={options =>
                                handleRoleChange(options, 'eligibleNations')
                            }
                        />
                    </FormField>

                    <FormField name='passports' label='Passports Held'>
                        <DropdownWithCheckBox
                            label='Select Passports'
                            defaultOptions={userInfo.passports || []}
                            valueKey='code'
                            labelKey='name'
                            maxSelection={3}
                            listOfOptions={ListOfCountries || []}
                            onChange={options =>
                                handleRoleChange(options, 'passports')
                            }
                        />
                    </FormField>
                </>
            )}

            {userInfo.userType !== 'NON_PLAYER' && (
                <div className='relative w-full mb-7 group'>
                    <FormField
                        name='representedBy'
                        label='Represented by'
                        value={values?.representedBy}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder='eg. Manager/Agent (Leave blank if not applicable)'
                        error={errors.representedBy}
                        touched={touched.representedBy}
                    />

                    <div>
                        <SwitchButton
                            inActiveBgColor='bg-white border border-black'
                            labelPosition='left'
                            text='Open to trials'
                            uppercaseText={false}
                            textFont='font-semibold'
                            checked={values?.openToTrials}
                            ballActiveColor='after:bg-black'
                            checkedBgColorClass='checked-green-bg'
                            small={true}
                            className='justify-start mt-5 gap-4'
                            handleCheckboxChange={event => {
                                handleChange({
                                    target: {
                                        name: 'openToTrials',
                                        value: event.target.checked
                                    }
                                });
                            }}
                        />
                        <p className='text-[10px] text-gray-500 mt-1'>
                            If you are interested in getting invites to trials
                            arranged by PLAYER, let us know.
                        </p>
                    </div>
                </div>
            )}

            {userInfo.userType === 'NON_PLAYER' && (
                <>
                    <FormField
                        name='nonPlayerRole'
                        label='Role(s)'
                        error={errors.nonPlayerRole}
                        touched={touched.nonPlayerRole}
                        required
                    >
                        <DropdownWithCheckBox
                            label='Select Your Role(s)'
                            maxSelection={3}
                            defaultOptions={userInfo.nonPlayerRole || []}
                            listOfOptions={NON_PLAYER_ROLE_OPTIONS}
                            valueKey='value'
                            labelKey='label'
                            onChange={options =>
                                handleRoleChange(options, 'nonPlayerRole')
                            }
                        />
                    </FormField>
                    <FormField
                        type='textarea'
                        name='bio'
                        label='Bio'
                        value={values?.bio}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder='Write Bio...'
                        error={errors.bio}
                        touched={touched.bio}
                        helpText='Tell us about yourself (10-500 characters)'
                    />
                </>
            )}
        </section>
    );
};

export default AvailabilitySection;
